import React, { useState, useContext, useEffect } from 'react';
import Modal from '../../common/Modal';
import { isOptionType } from '../../fields/constants/subtypes';
import { QueryContext } from '../../../hooks/Contexts';
import {
  exportFilteredCounts,
  exportOptionCounts,
  exportOptionsTemplate,
} from '../../options/constants/handleOption';
import { confirmation, notify } from '../../../helpers/notifications';
import { downloadAllEvents } from '../../metadata/constants/handleCalendarEvents';
import { handleFetchFieldDetails } from '../../fields/constants/handleField';
import { getFilenameFromHeader } from '../../../helpers/filename-from-header';
import Icon from '../../common/Icon';
import CSVUploadTipsModal from '../../options/CSVUploadTipsModal';
import { downloadBlob } from '../../../helpers/downloadBlob';

const CountExportModal = ({
  viewCountExport,
  setViewCountExport,
  fieldsets,
  formID,
  informant,
}) => {
  const { queryParams } = useContext(QueryContext);
  const [selectedField, setSelectedField] = useState(null);
  const [fieldDetails, setFieldDetails] = useState(null);
  const [groupByInformant, setGroupByInformant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCSVTips, setShowCSVTips] = useState(false);
  const groupable =
    informant &&
    selectedField &&
    fieldDetails?.field &&
    !fieldDetails?.field?.informs_global;
  const calendarAvailable = fieldDetails?.options?.some(
    (o) => o.calendar_event
  );
  const handleCSVToggle = (e) => {
    setShowCSVTips(!showCSVTips);
  };

  const handleGroupByToggle = (e) => {
    setGroupByInformant(e.target.checked);
  };

  const fetchField = async () => {
    let fetched = await handleFetchFieldDetails(selectedField);
    if (fetched.ok) {
      const { field, options, meta_entries } = fetched.data;
      setFieldDetails({ field, options, meta_entries });
      if (field.informs_global) setGroupByInformant(false);
    } else {
      setFieldDetails(null);
      notify.failure();
      console.error('failed to load field', fetched);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (selectedField) {
      setLoading(true);
      fetchField();
    } else {
      setGroupByInformant(false);
      setFieldDetails(null);
    }
  }, [selectedField]);

  const handleCountExport = async () => {
    let url = '/manage/submissions/count_export?';
    let filters = queryParams.filters
      ? JSON.stringify(queryParams.filters)
      : '';

    let params = new URLSearchParams({
      form_id: formID,
      field_id: selectedField,
      group_by_informant: groupByInformant,
      filters,
    }).toString();

    await exportFilteredCounts(url + params).then((success) => {
      if (success) handleClose();
    });
  };

  const handleClose = () => {
    setSelectedField(null);
    setFieldDetails(null);
    setViewCountExport(false);
    setGroupByInformant(false);
    setLoading(false);
  };

  const handleDetailed = async () => {
    if (fieldDetails && !loading) {
      const { field, options } = fieldDetails;
      exportOptionCounts({ field, options });
      notify.success();
      handleClose();
    } else {
      notify.failure();
      console.error('failed to load field', fieldDetails);
    }
  };

  const handleTemplate = async () => {
    if (fieldDetails && !loading) {
      const { field, options, meta_entries } = fieldDetails;
      let includeIcalColumns = options?.some((o) => o.calendar_event);
      const exportAndCleanup = () => {
        exportOptionsTemplate(
          { field, options, metaEntries: meta_entries },
          includeIcalColumns
        );
        notify.success();
        handleClose();
      };
      if (!includeIcalColumns && ['select', 'radio'].includes(field.type)) {
        confirmation.acknowledge(
          function yesClicked() {
            includeIcalColumns = true;
            exportAndCleanup();
          },
          function noClicked() {
            exportAndCleanup();
          },
          'Add ical columns to template?',
          "If you intend to generate and send ICS calendar attachments based on this field, click 'Yes'. Otherwise, disregard this message by clicking 'No'."
        );
      } else {
        exportAndCleanup();
      }
    } else {
      notify.failure();
      console.error('failed to load field', fieldDetails);
    }
  };

  const handleCalendarEvents = async () => {
    confirmation.prompt(
      'Allow Notifications?',
      'Enter your email to be notified of events that have been set to send alerts or leave blank to ignore alerts:',
      '',
      async (email) => {
        const ics = await downloadAllEvents(selectedField, email);
        if (ics.ok) {
          let cd = ics.headers['content-disposition'];
          let filename =
            getFilenameFromHeader(cd) || 'TXSSC: Unknown Calendar.ics';
          downloadBlob(new Blob([ics.data]), filename);
        }
      }
    );
  };

  const trStyle = {
    background: '#fff',
    cursor: 'default',
  };

  return (
    <Modal
      show={viewCountExport}
      onClose={handleClose}
      title='submission-count-exporter'
      ignoreClose={showCSVTips}
    >
      <header>
        <h3>Export Submission Totals / Field Options</h3>
      </header>
      <div className='submission-exporter'>
        <div className='builder'>
          <div className='creation-form'>
            <div className='row'>
              <div className='full'>
                <div className='select-input'>
                  <select
                    name='count-field-select'
                    id='count-field-select'
                    onChange={(e) => setSelectedField(e.target.value)}
                  >
                    <option label='Please Select Field' value='' />
                    {fieldsets.map((fs) => {
                      return (
                        <optgroup key={fs.id + '-fs-count'} label={fs.name}>
                          {fs.fields
                            .filter((f) => isOptionType(f.type))
                            .map((field) => {
                              return (
                                <option
                                  key={field.id + '-count'}
                                  value={field.id}
                                >
                                  {field.name}
                                </option>
                              );
                            })}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='controls'>
          <table>
            <thead>
              <tr style={trStyle} className='ignore-hover'>
                <th style={{ background: '#007096', color: '#fff' }}>Export</th>
                <th style={{ background: '#007096', color: '#fff' }}>
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={trStyle} className='ignore-hover'>
                <td style={{ borderRight: 'none' }}>
                  <button
                    disabled={!selectedField}
                    onClick={handleCountExport}
                    style={{
                      borderRadius: '6px',
                      width: '100%',
                      transition: 'background 0.5s ease',
                    }}
                  >
                    Filtered Count
                  </button>
                </td>
                <td style={{ paddingLeft: '18px' }}>
                  Simple count of submissions per option with search filters
                  applied (if any).
                  {groupable && (
                    <div className='checkbox-group'>
                      <input
                        type='checkbox'
                        id='GroupByInformant'
                        checked={groupByInformant}
                        onChange={handleGroupByToggle}
                        disabled={loading}
                      />
                      <label htmlFor='GroupByInformant'>
                        {' '}
                        Group by '{informant.name}'
                      </label>
                    </div>
                  )}
                </td>
              </tr>
              <tr style={trStyle} className='ignore-hover'>
                <td style={{ borderRight: 'none' }}>
                  <button
                    disabled={!selectedField}
                    onClick={handleDetailed}
                    style={{
                      borderRadius: '6px',
                      width: '100%',
                      transition: 'background 0.5s ease',
                    }}
                  >
                    All Options Detail Count
                  </button>
                </td>
                <td style={{ paddingLeft: '18px' }}>
                  Total counts for each option with option id's and quota info.
                </td>
              </tr>
              <tr style={trStyle} className='ignore-hover'>
                <td style={{ borderRight: 'none' }}>
                  <button
                    disabled={!selectedField}
                    onClick={handleTemplate}
                    style={{
                      borderRadius: '6px',
                      width: '100%',
                      transition: 'background 0.5s ease',
                    }}
                  >
                    Template for Edits
                  </button>
                </td>
                <td style={{ paddingLeft: '18px' }}>
                  All option data, including metadata, in a format that is ready
                  to receive edits.{' '}
                  <Icon
                    icon='question-circle'
                    onClick={(e) => handleCSVToggle(e)}
                    title='CSV Upload Tips'
                  />
                </td>
              </tr>
              {/* If field has calendar options */}
              <tr style={trStyle} className='ignore-hover'>
                <td style={{ borderRight: 'none' }}>
                  <button
                    disabled={!selectedField || !calendarAvailable}
                    onClick={handleCalendarEvents}
                    style={{
                      borderRadius: '6px',
                      width: '100%',
                      transition: 'background 0.5s ease',
                    }}
                  >
                    Calendar File
                  </button>
                </td>
                <td style={{ paddingLeft: '18px' }}>
                  All available calendar events from this field's options.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <CSVUploadTipsModal show={showCSVTips} close={handleCSVToggle} />
    </Modal>
  );
};

export default CountExportModal;
